import type { ApolloClient } from '@apollo/client';
import type {
  ArchiveByIdsMutationResult,
  ArchiveByIdsMutationVariables,
  ScenarioTimeline_SingleEventFragment,
} from '../../graphql';
import { ArchiveByIdsDocument } from '../../graphql';
import type { ScenarioOrder } from '../../lib/planningData';
import type { Order } from './Core';
import type { ClientId } from './ClientId';
import { parseClientId } from './ClientId';
import moment from 'moment';
import type { SetStateAction, Dispatch } from 'react';
import { DefaultMap } from '@seaber/common/lib/collections';
import { minBy } from 'lodash';

export async function archiveByIds(
  client: ApolloClient<unknown>,
  orderIds: number[],
  portCallIds: number[],
  scheduledOrderIds: number[],
  handledBySpotIds: number[],
  handledByCoaIds: number[],
  singleEventIds: number[],
  offHireIds: number[],
) {
  return client.mutate<
    ArchiveByIdsMutationResult['data'],
    ArchiveByIdsMutationVariables
  >({
    mutation: ArchiveByIdsDocument,
    variables: {
      orderIds,
      portCallIds,
      scheduledOrderIds,
      handledBySpotIds,
      handledByCoaIds,
      singleEventIds,
      offHireIds,
    },
  });
}

export function handleSelectBefore(
  orders: ScenarioOrder[],
  singleEvents: ReadonlyArray<ScenarioTimeline_SingleEventFragment>,
  date: Date,
  setSelectedOrderIds: (newVal: Order['id'][]) => void,
  setTimelineSelection: Dispatch<
    SetStateAction<{
      selectedOrderIds: Set<number>;
      selectedEventIds: Set<string | number>;
    }>
  >,
  setCompletedBeforeDate: (
    value:
      | ((prevState: Date | undefined) => Date | undefined)
      | Date
      | undefined,
  ) => void,
) {
  const incompletePortCalls = new Set<number>();
  const incompleteShipmentIds = new Set<ClientId>();
  const completedOrders = new Set<ScenarioOrder>();
  const completedEvents = new Set<ScenarioTimeline_SingleEventFragment>();
  const completedOrdersByPortCall = new DefaultMap<number, Set<ScenarioOrder>>(
    () => new Set<ScenarioOrder>(),
  );
  const shipmentRangeByKeys = new DefaultMap<
    number,
    Map<ClientId, { start: number; complete: number }>
  >(() => new Map());

  /*
  E.g. porvoo-tallinn-porvoo-kalmar,
  where p-t order is completed, p-k order is not,
  and t-p order has port call in common with both.

  Should not archive any of the orders or port calls,
  to preserve calculated times on commit, and,
  not to create any port calls + replace relations.
  */
  function removeUntilBallast(pcId: number) {
    if (completedOrdersByPortCall.has(pcId)) {
      for (const o of completedOrdersByPortCall.get(pcId)) {
        if (!completedOrders.has(o)) {
          continue;
        }
        completedOrders.delete(o);
        for (const pc of o.orderPortCallRelationsByOrderId.nodes) {
          const portCallId = pc?.portCallId;
          if (
            portCallId !== undefined &&
            !incompletePortCalls.has(portCallId)
          ) {
            incompletePortCalls.add(portCallId);
            removeUntilBallast(portCallId);
          }
        }
      }
    }
  }

  for (const o of orders) {
    const isBefore = moment(o.completed).isBefore(date);
    const noIncompletePortCalls = o.orderPortCallRelationsByOrderId.nodes.every(
      (pc) => {
        const pcId = pc?.portCallId;
        return pcId !== undefined && !incompletePortCalls.has(pcId);
      },
    );

    const { started, completed, shipmentId } = o;
    const shipId = o.scheduledOrdersByOrderId.nodes[0]?.vesselId;
    if (shipId && shipmentId && started && completed) {
      const currentRange = shipmentRangeByKeys.get(shipId).get(shipmentId);
      if (currentRange) {
        currentRange.start = Math.min(currentRange.start, +moment(started));
        currentRange.complete = Math.max(
          currentRange.complete,
          +moment(completed),
        );
      } else {
        shipmentRangeByKeys.get(shipId).set(shipmentId, {
          start: +moment(started),
          complete: +moment(completed),
        });
      }
    }

    if (isBefore && noIncompletePortCalls) {
      for (const pc of o.orderPortCallRelationsByOrderId.nodes) {
        const pcId = pc?.portCallId;
        if (pcId !== undefined) {
          completedOrdersByPortCall.get(pcId).add(o);
        }
      }
      completedOrders.add(o);
    } else {
      if (o.shipmentId !== null) {
        incompleteShipmentIds.add(o.shipmentId);
      }

      for (const pc of o.orderPortCallRelationsByOrderId.nodes) {
        const pcId = pc?.portCallId;
        if (pcId !== undefined) {
          incompletePortCalls.add(pcId);
          removeUntilBallast(pcId);
        }
      }
    }
  }

  /** earliest incomplete shipment by vessel id */
  const earliestInCompletedShipmentRangeByVesselId = new Map(
    [...shipmentRangeByKeys].map(([vesselId, shipmentRangeByShipmentId]) => {
      const incompleteRanges = [...shipmentRangeByShipmentId]
        .filter(([shipmentId]) => incompleteShipmentIds.has(shipmentId))
        .map(([, timeRange]) => timeRange);

      const earliestRange = minBy(incompleteRanges, (range) => range.start);

      return [vesselId, earliestRange];
    }),
  );

  for (const se of singleEvents) {
    if (!moment(se.start).isBefore(date)) {
      continue;
    }
    const hasIncompletePortCalls =
      se.singleEventPortCallRelationsBySingleEventId.nodes.every((pc) => {
        const pcId = pc?.portCallId;
        return pcId !== undefined && incompletePortCalls.has(pcId);
      });
    if (hasIncompletePortCalls) {
      continue;
    }

    if (!se.vesselId) {
      console.error('Single event without vessel id', se);
      continue;
    }

    const earliestIncompleteRange =
      earliestInCompletedShipmentRangeByVesselId.get(se.vesselId);

    if (!earliestIncompleteRange) {
      completedEvents.add(se);
      continue;
    }

    const isEventStartWithinIncompleteRange = moment(se.start).isBetween(
      earliestIncompleteRange.start,
      earliestIncompleteRange.complete,
    );

    const isEventCompleteWithinIncompleteRange = moment(se.end).isBetween(
      earliestIncompleteRange.start,
      earliestIncompleteRange.complete,
    );

    const isOverlap =
      isEventStartWithinIncompleteRange || isEventCompleteWithinIncompleteRange;

    if (!isOverlap) {
      completedEvents.add(se);
    }
  }

  const orderIds = Array.from(completedOrders)
    .filter((o) => {
      const shipmentId = o.shipmentId;
      return shipmentId === null || !incompleteShipmentIds.has(shipmentId);
    })
    .map((o) => o.id);
  const eventClientIds = Array.from(completedEvents).map(
    (e) => `single-${parseClientId(e.clientId)}`,
  );
  setSelectedOrderIds(orderIds);
  setTimelineSelection((prevState) => ({
    ...prevState,
    selectedOrderIds: new Set(orderIds),
    selectedEventIds: new Set(eventClientIds),
  }));
  setCompletedBeforeDate(date);
}
